import type { CachedUser } from 'src/auth/coldOpenPlan'

export const IMPERSONATION_RESTORE_KIND = 'impersonation-restore' as const

/** Dedicated key. Never `fuellox.offlineQueue.snapshot`. Native drain hardcodes that one key. */
export const IMPERSONATION_RESTORE_KEY = 'fuellox.impersonation.restore'

export type ImpersonationRestore = {
  readonly kind: typeof IMPERSONATION_RESTORE_KIND
  readonly staffToken: string
  readonly impersonatorName: string
  readonly targetName: string
  readonly cachedUser: CachedUser
}

export type ImpersonationChrome =
  | { readonly active: false }
  | {
      readonly active: true
      readonly impersonatorName: string
      readonly targetName: string
    }

export function chromeFromRestore (
  restore: ImpersonationRestore | null,
): ImpersonationChrome {
  if (restore === null) {
    return { active: false }
  }
  return {
    active: true,
    impersonatorName: restore.impersonatorName,
    targetName: restore.targetName,
  }
}

export function displayNameFromCachedUser (user: CachedUser | null): string {
  const name = user && typeof user.name === 'string' ? user.name.trim() : ''
  return name === '' ? 'Staff' : name
}

export function captureStaffRestore (input: {
  snapshot: { token: string, cachedUser: CachedUser | null }
  targetName: string
}): ImpersonationRestore {
  const staffToken = input.snapshot.token.trim()
  if (staffToken === '') {
    throw new Error('Cannot start impersonation without a staff token')
  }
  const targetName = input.targetName.trim()
  if (targetName === '') {
    throw new Error('Cannot start impersonation without a target name')
  }
  const cachedUser = input.snapshot.cachedUser ?? {}
  return {
    kind: IMPERSONATION_RESTORE_KIND,
    staffToken,
    impersonatorName: displayNameFromCachedUser(input.snapshot.cachedUser),
    targetName,
    cachedUser,
  }
}

export function parseImpersonationRestore (raw: string | null | undefined): ImpersonationRestore | null {
  if (typeof raw !== 'string' || raw === '') {
    return null
  }
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return null
  }
  const record = parsed as Record<string, unknown>
  if (record.kind !== IMPERSONATION_RESTORE_KIND) {
    return null
  }
  if (typeof record.staffToken !== 'string' || record.staffToken.trim() === '') {
    return null
  }
  if (typeof record.targetName !== 'string' || record.targetName.trim() === '') {
    return null
  }
  if (typeof record.impersonatorName !== 'string') {
    return null
  }
  if (
    record.cachedUser === null
    || typeof record.cachedUser !== 'object'
    || Array.isArray(record.cachedUser)
  ) {
    return null
  }
  return {
    kind: IMPERSONATION_RESTORE_KIND,
    staffToken: record.staffToken.trim(),
    impersonatorName: record.impersonatorName,
    targetName: record.targetName.trim(),
    cachedUser: record.cachedUser as CachedUser,
  }
}

export function serializeImpersonationRestore (restore: ImpersonationRestore): string {
  return JSON.stringify({
    kind: restore.kind,
    staffToken: restore.staffToken,
    impersonatorName: restore.impersonatorName,
    targetName: restore.targetName,
    cachedUser: restore.cachedUser,
  })
}
